import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../../core/services/auth.service';
import { ReturnUrlService } from '../../../core/services/return-url.service';
import { ApiResponse, AuthResult } from '../../../core/models/auth.models';
import { ErrorResponse } from '../../../core/models/error.models';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-saml-validate',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './saml-validate.component.html',
  styleUrl: './saml-validate.component.scss',
})
export class SamlValidateComponent implements OnInit, OnDestroy {
  isLoading = false;
  errorMessage = '';
  private readonly destroy$ = new Subject<void>();
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly returnUrlService = inject(ReturnUrlService);
  private readonly USER_KEY = 'user_data';
  private readonly STORAGE_KEY = 'returnUrl';

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const error = params['error'];
      const client = params['client'];
      const code = params['code'];
      const returnUrl = localStorage.getItem(this.STORAGE_KEY);
      if (error && client !== 'mobile') {
        this.router.navigate(['/login'], {
          queryParams: { errorMessage: error, returnUrl: returnUrl || null },
        });
        return;
      }
      if (!client) {
        console.error('Client parameter is missing');
        this.router.navigate(['/login']);
        return;
      }

      if (client === 'mobile') {
        this.handleDeepLinking(code, error);
      } else if (client === 'web') {
        this.signIn();
      }
    });
  }

  signIn(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.authService
      .loginWithoutOtp()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: ApiResponse<AuthResult>) => {
          const returnUrl = this.returnUrlService.getReturnUrl();
          localStorage.removeItem('verify-user');
          localStorage.removeItem('isOtpVerified');

          if (res.result.isSuperAdmin) {
            this.returnUrlService.clearReturnUrl();
            globalThis.location.href = returnUrl;
          } else {
            const user = localStorage.getItem(this.USER_KEY);
            if (user) {
              this.authService.userData.next(
                this.authService.decryptData(user)
              );
            }
            const roles = res.result.roles || [];
            this.authService.setRole(roles || []).then(() => {
              this.authService.logoutLMS();
              const returnUrl = this.returnUrlService.getReturnUrl();
              this.returnUrlService.clearReturnUrl();
              const finalUrl =
                returnUrl && returnUrl === this.router.url
                  ? '/dashboard'
                  : returnUrl || '/dashboard';
              globalThis.location.href = finalUrl;
            });
          }
        },
        error: (error: ErrorResponse) => {
          this.isLoading = false;
          const errorMsg = error.errors?.[0]?.description
            ? error.errors[0].description
            : 'Authentication failed. Please try again.';
          this.errorMessage = errorMsg;
          this.router.navigate(['/login'], {
            queryParams: { errorMessage: errorMsg },
          });
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  handleDeepLinking(code: string, error: string | null): void {
    try {
      if (error) {
        this.errorMessage = `Error received: ${error}`;
        console.error('[Deep Link] Error received:', error);
        const deepLinkUrl = `itb://auth/saml/callback?errorMessage=${error}`;
        globalThis.location.replace(deepLinkUrl);
        return;
      }
      if (!code) {
        this.errorMessage = 'Authorization code not found';
        const deepLinkUrl = `itb://auth/saml/callback?errorMessage=${this.errorMessage}`;
        globalThis.location.replace(deepLinkUrl);
        return;
      }
      const deepLinkUrl = `itb://auth/saml/callback?code=${encodeURIComponent(code)}`;

      globalThis.location.replace(deepLinkUrl);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      const deepLinkUrl = `itb://auth/saml/callback?errorMessage=${errorMsg}`;
      this.errorMessage = `Failed to process deep link: ${errorMsg}`;
      globalThis.location.replace(deepLinkUrl);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
